import { defineMessages, useIntl, FormattedMessage } from "react-intl";
import { useEffect } from "react";

import { useDispatch, useSelector } from "redux/util";
import { forgotPassword } from "redux/actions/authentication";
import Button from "common/core/button";
import {
  FormattedFieldError,
  isAriaInvalid,
  defaultRequiredMessage,
  emailPatternValidation,
} from "common/core/form/error";
import { StyledTextInput } from "common/core/form/text";
import { useForm } from "common/core/form";
import SROnly from "common/core/screen_reader";
import { useId } from "util/html";

import { ForgotPasswordHeader, BackButton } from "./components";
import Styles from "./index.module.scss";

type FormValues = {
  email: string;
};

type Props = {
  onBack: () => void;
};

const MESSAGES = defineMessages({
  emailPlaceholder: {
    id: "d5621515-9f9b-483f-8a06-7034eefefc2a",
    defaultMessage: "Email address",
  },
  backButtonAriaLabel: {
    id: "8fe12575-d2a1-4930-a2b3-434a890e5124",
    defaultMessage: "Back to email input",
  },
});

export default function ForgotPasswordScreen({ onBack }: Props) {
  const authentication = useSelector((state) => state.authentication);
  const error = !Object.keys(authentication.error).length ? null : authentication.error;
  const intl = useIntl();

  const id = useId();

  const dispatch = useDispatch();

  const onSendPasswordReset = (formValues: FormValues) => {
    dispatch(forgotPassword(formValues.email));
  };

  const {
    handleSubmit,
    register,
    formState: { errors, isValid, isSubmitting, isSubmitSuccessful },
    setFocus,
  } = useForm<FormValues>({
    mode: "all",
    defaultValues: {
      email: "",
    },
  });

  useEffect(() => {
    setFocus("email");
  }, [setFocus]);

  return (
    <>
      <ForgotPasswordHeader />
      <SROnly>
        <p id={id}>
          Enter your email address to receive instructions on how to reset your password
        </p>
      </SROnly>
      <form className={Styles.form} onSubmit={handleSubmit(onSendPasswordReset)}>
        <StyledTextInput
          aria-invalid={isAriaInvalid(errors.email)}
          aria-labelledby={id}
          className={errors.email ? Styles.marginBottom : Styles.input}
          label={intl.formatMessage(MESSAGES.emailPlaceholder)}
          data-automation-id="email-field"
          type="email"
          {...register("email", {
            required: defaultRequiredMessage(intl),
            pattern: emailPatternValidation(intl),
          })}
        />
        {errors.email && (
          <div className={Styles.marginBottom}>
            <FormattedFieldError inputName={"email"} error={errors.email} />
          </div>
        )}
        <Button
          className={Styles.marginBottom}
          type="submit"
          disabled={!isValid || isSubmitSuccessful}
          buttonColor="action"
          buttonSize="large"
          variant="primary"
          fullwidth
        >
          {isSubmitting && (
            <p>
              <FormattedMessage
                id="190eab47-23b7-4712-b241-22c4224e11a2"
                defaultMessage="Sending"
              />
            </p>
          )}
          {isSubmitSuccessful && (
            <p>
              <FormattedMessage
                id="072d2ae9-7f29-464d-8358-0be9b273e047"
                defaultMessage="Reset link sent"
              />
            </p>
          )}
          {!isSubmitting && !isSubmitSuccessful && (
            <FormattedMessage
              id="686224f2-8b05-4871-91fb-21f00f8aac21"
              defaultMessage="Send password reset link"
            />
          )}
        </Button>
      </form>
      {error && (
        <p className={Styles.error}>
          <FormattedMessage
            id="f6e97eba-c75c-4e2c-b225-e2d98f6db9d1"
            defaultMessage="* Invalid email"
          />
        </p>
      )}
      <BackButton onBack={onBack} aria-label={intl.formatMessage(MESSAGES.backButtonAriaLabel)} />
    </>
  );
}
